import { useEffect } from 'react';

const PerformanceOptimizer = () => {
  useEffect(() => {
    // Preload critical resources
    const preloadCriticalResources = () => {
      // Preload fonts
      const fontLink = document.createElement('link');
      fontLink.rel = 'preload';
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
      fontLink.as = 'style';
      document.head.appendChild(fontLink);

      // Preload critical images
      const criticalImages = [
        'https://images.unsplash.com/photo-1709715357520-5e1047a2b691', // Hero image
        'https://images.unsplash.com/photo-1558494949-ef010cbdcc31', // IT Infrastructure
        'https://images.unsplash.com/photo-1563013544-824ae1b704d3', // Security
      ];

      criticalImages.forEach(src => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = src;
        link.as = 'image';
        document.head.appendChild(link);
      });
    };

    // Optimize third-party scripts
    const optimizeThirdPartyScripts = () => {
      // Add loading="lazy" to iframes if any
      const iframes = document.querySelectorAll('iframe');
      iframes.forEach(iframe => {
        if (!iframe.hasAttribute('loading')) {
          iframe.setAttribute('loading', 'lazy');
        }
      });

      // Defer non-critical scripts
      const scripts = document.querySelectorAll('script[src]');
      scripts.forEach(script => {
        if (!script.hasAttribute('async') && !script.hasAttribute('defer')) {
          script.setAttribute('defer', '');
        }
      });
    };

    // Implement resource hints
    const addResourceHints = () => {
      // DNS prefetch for external domains
      const externalDomains = [
        'images.unsplash.com',
        'fonts.googleapis.com',
        'fonts.gstatic.com'
      ];

      externalDomains.forEach(domain => {
        const link = document.createElement('link');
        link.rel = 'dns-prefetch';
        link.href = `https://${domain}`;
        document.head.appendChild(link);
      });

      // Preconnect to critical domains
      const criticalDomains = ['images.unsplash.com'];
      criticalDomains.forEach(domain => {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = `https://${domain}`;
        document.head.appendChild(link);
      });
    };

    // Optimize images globally
    const optimizeImages = () => {
      const images = document.querySelectorAll('img');
      images.forEach(img => {
        // Add loading="lazy" if not already set
        if (!img.hasAttribute('loading') && !img.closest('.hero')) {
          img.setAttribute('loading', 'lazy');
        }

        // Add decoding="async" for better performance
        if (!img.hasAttribute('decoding')) {
          img.setAttribute('decoding', 'async');
        }

        // Add proper alt attributes if missing
        if (!img.hasAttribute('alt')) {
          img.setAttribute('alt', '');
        }
      });
    };

    // Memory management
    const setupMemoryManagement = () => {
      // Clean up unused resources on navigation
      const cleanupResources = () => {
        // Remove unused images from memory
        const images = document.querySelectorAll('img');
        images.forEach(img => {
          if (!img.complete && img.loading === 'lazy') {
            const rect = img.getBoundingClientRect();
            if (rect.bottom < -200 || rect.top > window.innerHeight + 200) {
              img.src = img.dataset.src || img.src;
            }
          }
        });
      };

      // Run cleanup on scroll (throttled)
      let scrollTimeout;
      const handleScroll = () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(cleanupResources, 500);
      };

      window.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    };

    // Web Vitals monitoring and optimization
    const optimizeWebVitals = () => {
      // Optimize LCP (Largest Contentful Paint)
      const optimizeLCP = () => {
        // Ensure hero images load quickly
        const heroImages = document.querySelectorAll('.hero img, [data-priority="high"] img');
        heroImages.forEach(img => {
          img.setAttribute('loading', 'eager');
          img.setAttribute('fetchpriority', 'high');
        });
      };

      // Optimize FID (First Input Delay)
      const optimizeFID = () => {
        // Use passive listeners for scroll events
        const passiveEvents = ['scroll', 'touchstart', 'touchmove'];
        passiveEvents.forEach(event => {
          document.addEventListener(event, () => {}, { passive: true });
        });
      };

      // Optimize CLS (Cumulative Layout Shift)
      const optimizeCLS = () => {
        // Add aspect ratio to images without dimensions
        const images = document.querySelectorAll('img:not([width]):not([height])');
        images.forEach(img => {
          img.style.aspectRatio = '16/9'; // Default aspect ratio
        });
      };

      optimizeLCP();
      optimizeFID();
      optimizeCLS();
    };

    // Run optimizations
    const runOptimizations = () => {
      preloadCriticalResources();
      addResourceHints();
      
      // Delay non-critical optimizations
      setTimeout(() => {
        optimizeThirdPartyScripts();
        optimizeImages();
        optimizeWebVitals();
      }, 100);

      return setupMemoryManagement();
    };

    const cleanup = runOptimizations();

    return cleanup;
  }, []);

  return null; // This component doesn't render anything
};

export default PerformanceOptimizer;